import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewEncapsulation,
  DOCUMENT,
} from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, ResolveEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { ModernLayoutComponent } from './layouts/horizontal/modern/modern.component';
import { OnboardingLayoutComponent } from './layouts/onboarding/onboarding.component';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { UserStore } from 'app/core/services/user.store';
import { GettingStartedPanelComponent } from 'app/shared/components/getting-started/getting-started-panel.component';
import { GettingStartedService } from 'app/shared/components/getting-started/getting-started.service';
import { BottomBarService } from 'app/shared/services/bottom-bar.service';

/** Page templates that render inside the chrome-less onboarding shell instead of the app layout. */
const ONBOARDING_SHELL_TEMPLATES = ['onboarding-status'];

/**
 * Page templates that ARE the dashboard. The dashboard has no route constant — it is a data-driven
 * page module reached through the generic ':name' route — so its URL is whatever slug the admin gave
 * it. The template is the stable identifier, exactly as with ONBOARDING_SHELL_TEMPLATES above.
 *
 * A `string[]` to leave room for multiple dashboard-style templates without touching the
 * filter below.
 */
const DASHBOARD_PAGE_TEMPLATES = ['dashboard'];

/**
 * Layouts that render the full app chrome. The floating "Aan de slag" panel only belongs in these:
 * 'empty' is the auth/error shell and 'onboarding' is the chrome-less registration surface, and
 * neither should offer app affordances.
 */
const APP_SHELL_LAYOUTS = ['classy', 'modern'];

/**
 * How long the app waits for the module list before rendering a layout anyway. Only reached when
 * the module load fails or hangs — the gate normally opens as soon as the list lands.
 */
const SHELL_GATE_TIMEOUT_MS = 5000;

@Component({
  selector: 'layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmptyLayoutComponent,
    ClassyLayoutComponent,
    ModernLayoutComponent,
    OnboardingLayoutComponent,
    GettingStartedPanelComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  readonly #modulesStore = inject(ModulesStore);
  readonly #userStore = inject(UserStore);
  readonly #cd = inject(ChangeDetectorRef);

  /**
   * Owns visibility, live completion state and the persisted collapse/skip/dismiss choices of the
   * global "Aan de slag" widgets. Protected rather than private: the template wires the panel's
   * outputs straight to it, so there are no pass-through methods on this component.
   */
  protected readonly gettingStarted = inject(GettingStartedService);

  config: FuseConfig;
  layout: string;

  /**
   * Whether the shell for the current URL is known yet.
   *
   * A page module can claim its own shell, which is only knowable once the module list has loaded.
   * Each layout renders its OWN `<router-outlet>`, so putting the routed component up under a
   * provisional shell and flipping afterwards destroys and re-creates it — on `/onboarding` that
   * means the entry lookup and its poll run twice, plus a visible remount.
   *
   * Rendering waits for that answer, with two escapes so a wait can never become a blank app:
   * the 'empty' layout (auth routes never resolve module data), and {@link #shellGateExpired}.
   */
  shellReady = false;

  /**
   * Releases the shellReady gate when the module list never arrives.
   *
   * A failed module load leaves `modulesLoaded` false forever (modules.feature.ts only clears
   * `isLoading` on the error path), and `isLoading` itself is unusable as the escape: it is shared
   * with entries and taxonomy loads, so an unrelated load finishing first would open the gate
   * before the modules are in — exactly the flip this gate exists to prevent. A plain deadline is
   * the only signal here that means what it says.
   */
  readonly #shellGateExpired = signal(false);
  #shellGateTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * URL of the navigation being resolved, set between ResolveEnd and NavigationEnd. During that
   * window `Router.url` and `_activatedRoute` still describe the PREVIOUS page, so the data-driven
   * layout has to be derived from this instead.
   */
  #pendingUrl: string | null = null;

  /**
   * The URL the app is on, mirrored into a signal so the floating "Aan de slag" panel can react to
   * navigation. `Router.url` is not reactive, and during the ResolveEnd window it still describes
   * the previous page — so this uses the same `#pendingUrl ?? Router.url` resolution as
   * {@link _resolveDataDrivenLayout}, refreshed from {@link _updateLayout}.
   */
  readonly #currentUrl = signal('');

  /** Whether the resolved layout renders the full app chrome — see APP_SHELL_LAYOUTS. */
  readonly #appShellActive = signal(false);

  /**
   * Slugs of every page module carrying a dashboard template. A set rather than a single slug: a
   * tenant may configure more than one dashboard page, and each of them renders its own banner.
   */
  readonly #dashboardSlugs = computed(
    () =>
      new Set(
        (this.#modulesStore.enabledPages() ?? [])
          .filter(page => DASHBOARD_PAGE_TEMPLATES.includes(page.template))
          .map(page => page.slug)
      )
  );

  /** First path segment of the current URL — for a data-driven page that is its slug. */
  readonly #currentSlug = computed(() => {
    const segment = this.#currentUrl().split('#')[0].split('?')[0].split('/').filter(Boolean)[0];
    return segment ? decodeURIComponent(segment) : null;
  });

  /**
   * On a dashboard the banner (mounted in DashboardTemplateComponent) already carries the panel, so
   * the floating copy stands down. Derived from the page's template rather than a '/dashboard'
   * literal: the slug is admin-configurable and the app has no route constant for it.
   */
  readonly #onDashboard = computed(() => {
    const slug = this.#currentSlug();
    return !!slug && this.#dashboardSlugs().has(slug);
  });

  /**
   * The global widget to render as the floating panel, or undefined when it must not appear at all:
   * outside the app chrome (empty/onboarding shells), for onboarding accounts (same rule as
   * HideForOnboardingDirective and the widget sidebar), on the dashboard, or when the service
   * reports no visible floating widget for this user.
   *
   * The service exposes methods rather than per-placement signals, so every read is wrapped in a
   * `computed()` here — the template must never call a function.
   */
  protected readonly floatingWidget = computed(() => {
    if (!this.#appShellActive() || this.#userStore.isOnboarding() || this.#onDashboard()) {
      return undefined;
    }
    const widget = this.gettingStarted.floatingWidget();
    return widget && this.gettingStarted.visible(widget) ? widget : undefined;
  });

  /** Undefined = render nothing. Doubles as the template's @if gate. */
  protected readonly floatingConfig = computed(() => this.gettingStarted.configFor(this.floatingWidget()));

  protected readonly floatingSteps = computed(() => this.gettingStarted.stepViewsFor(this.floatingWidget()));

  protected readonly floatingCollapsed = computed(() => this.gettingStarted.collapsed(this.floatingWidget()));

  readonly #bottomBar = inject(BottomBarService);

  /**
   * How far the floating panel has to move up to clear the table's bulk-actions bar, which spans
   * the full width the moment rows are selected and would otherwise bury it. 0 when no bar shows.
   */
  protected readonly floatingLift = this.#bottomBar.barHeight;

  scheme: 'dark' | 'light';
  theme: string;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private _document: any,
    private _renderer2: Renderer2,
    private _router: Router,
    private _fuseConfigService: FuseConfigService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fusePlatformService: FusePlatformService
  ) {
    // A page module can claim its own shell (see _resolveDataDrivenLayout), but on a cold load the
    // modules are still being fetched when NavigationEnd fires. Re-resolve whenever the page list
    // changes so the shell settles as soon as the data lands, without a second navigation.
    effect(() => {
      // Tracked ABOVE the guard on purpose: reading them only inside _updateLayout() would leave
      // them untracked on the runs that bail out early, so a module list arriving later would
      // never re-run this and the shellReady gate would stay shut.
      this.#modulesStore.enabledPages();
      this.#modulesStore.modulesLoaded();
      this.#shellGateExpired();
      if (!this.config) {
        return;
      }
      this._updateLayout();
      this.#cd.markForCheck();
    });

    this.#shellGateTimer = setTimeout(() => this.#shellGateExpired.set(true), SHELL_GATE_TIMEOUT_MS);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the theme and scheme based on the configuration
    combineLatest([
      this._fuseConfigService.config$,
      this._fuseMediaWatcherService.onMediaQueryChange$([
        '(prefers-color-scheme: dark)',
        '(prefers-color-scheme: light)',
      ]),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme,
          };

          // If the scheme is set to 'auto'...
          if (config.scheme === 'auto') {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
          }

          return options;
        })
      )
      .subscribe(options => {
        // Store the options
        this.scheme = options.scheme;
        this.theme = options.theme;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
      });

    // Subscribe to config changes
    this._fuseConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: FuseConfig) => {
      // Store the config
      this.config = config;

      // Update the layout
      this._updateLayout();
    });

    // Resolve the layout BEFORE the routed component is created. ResolveEnd is the last event
    // ahead of activation and fires after the data resolvers, so the modules are loaded and a
    // data-driven shell (see _resolveDataDrivenLayout) is already known. Settling it here matters:
    // switching layouts is a structural @if in the template, so a later switch would tear down the
    // first layout's router-outlet and re-activate the routed component — running its constructor,
    // its data loads and its pollers twice.
    this._router.events
      .pipe(
        filter((event): event is ResolveEnd => event instanceof ResolveEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(event => {
        this.#pendingUrl = event.urlAfterRedirects;
        if (!this.config) {
          return; // config$ has not emitted yet; NavigationEnd will settle the layout
        }
        this._updateLayout();
        this.#cd.markForCheck();
      });

    // Settle on NavigationEnd, and drop the pending URL again when a navigation never gets there.
    // A cancelled (superseded, guard-redirected) or failed navigation would otherwise leave
    // #pendingUrl pointing at a page that was never activated, and every later enabledPages()
    // emission would resolve the shell from that dead URL.
    this._router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
        ),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.#pendingUrl = null;
        // Update the layout
        this._updateLayout();
      });

    // Set the app version
    this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

    // Set the OS name
    this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    if (this.#shellGateTimer !== null) {
      clearTimeout(this.#shellGateTimer);
      this.#shellGateTimer = null;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Expand/collapse the floating "Aan de slag" panel. Also the "evaluate on opening the panel"
   * trigger from #20703 — only on expand, so collapsing does not fire a pointless round of count
   * requests.
   */
  protected onToggleGettingStarted(): void {
    const widget = this.floatingWidget();
    const wasCollapsed = this.gettingStarted.collapsed(widget);
    this.gettingStarted.toggle(widget);
    if (wasCollapsed) {
      this.gettingStarted.refresh({ force: true });
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');

    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach(path => {
      // Check if there is a 'layout' data
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
        // Set the layout
        this.layout = path.routeConfig.data.layout;
      }
    });

    // 4. Data-driven pages can claim their own shell. Page modules all resolve through the same
    // generic ':name' route, so their layout cannot live in route data — it is derived from the
    // page's configured template instead. Resolving it here rather than from inside the page
    // component keeps the shell correct on the very first render, so the app chrome never flashes.
    const pageLayout = this._resolveDataDrivenLayout();
    if (pageLayout) {
      this.layout = pageLayout;
    }

    this.shellReady = this.layout === 'empty' || this.#modulesStore.modulesLoaded() || this.#shellGateExpired();

    // Mirror the two inputs the floating panel gate needs into signals. Same resolution as
    // _resolveDataDrivenLayout: during ResolveEnd, Router.url is still the previous page.
    this.#currentUrl.set(this.#pendingUrl ?? this._router.url);
    this.#appShellActive.set(APP_SHELL_LAYOUTS.includes(this.layout));
  }

  /**
   * Layout requested by the page module in the URL being navigated to, or null when the first
   * segment is not a page, its template claims no shell of its own, or the caller passed an
   * explicit `?layout=` (an escape hatch for embedded/preview views, which outranks the page).
   *
   * Everything is read off the URL rather than off the activated route: this also runs on
   * ResolveEnd, where the activated route still describes the previous page, and the ':name' param
   * is declared on the lazy parent route so it is not guaranteed to reach the deepest snapshot.
   */
  private _resolveDataDrivenLayout(): string | null {
    const url = this.#pendingUrl ?? this._router.url;
    const [path, query] = url.split('?');
    if (new URLSearchParams(query ?? '').get('layout')) {
      return null;
    }
    const slug = path.split('#')[0].split('/').filter(Boolean)[0];
    if (!slug) {
      return null;
    }
    const page = this.#modulesStore.enabledPages()?.find(item => item.slug === decodeURIComponent(slug));
    return page && ONBOARDING_SHELL_TEMPLATES.includes(page.template) ? 'onboarding' : null;
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    // Remove class names for all schemes from both html and body
    this._document.documentElement.classList.remove('light', 'dark');
    this._document.body.classList.remove('light', 'dark');

    // Add class name for the currently selected scheme
    this._document.documentElement.classList.add(this.scheme);
    this._document.body.classList.add(this.scheme);

    // Persist resolved scheme for early theme detection on next page load
    try {
      localStorage.setItem('theme', this.scheme);
    } catch (e) {
      console.warn('Could not persist theme to localStorage', e);
    }
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    // Find the class name for the previously selected theme and remove it
    this._document.body.classList.forEach((className: string) => {
      if (className.startsWith('theme-')) {
        this._document.body.classList.remove(className, className.split('-')[1]);
      }
    });

    // Add class name for the currently selected theme
    this._document.body.classList.add(this.theme);
  }
}
